// src/components/NotFound.jsx
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  AppBar,
  Toolbar,
  Typography,
  Button,
  Container,
  Paper,
  IconButton,
  Menu,
  MenuItem,
  useTheme,
  CssBaseline,
  useMediaQuery
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import MenuIcon from '@mui/icons-material/Menu';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MailIcon from '@mui/icons-material/Mail';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthProvider';
import CompanyLogo from '../img/Athrv-white.png';

const NotFound = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState(null);

  // Define dark and light themes
  const darkTheme = createTheme({
    palette: {
      mode: 'dark',
      primary: {
        main: '#0C1A32',
      },
      background: {
        default: 'linear-gradient(135deg, #0C1A32, #002244)',
      },
      text: {
        primary: '#ffffff',
      },
    },
  });

  const lightTheme = createTheme({
    palette: {
      mode: 'light',
      primary: {
        main: '#0C1A32',
      },
      background: {
        default: '#f5f5f5',
      },
      text: {
        primary: '#000000',
      },
    },
  });

  // Use dark theme when not logged in; use light theme when logged in
  const appliedTheme = !isAuthenticated ? darkTheme : lightTheme;

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  return (
    <ThemeProvider theme={appliedTheme}>
      <CssBaseline />
      <Box
        sx={{
          minHeight: isAuthenticated ? 'auto' : '100vh',
          background: appliedTheme.palette.background.default,
          display: 'flex',
          flexDirection: 'column',
          color: appliedTheme.palette.text.primary,
        }}
      >
        {/* Header: Show only when NOT logged in */}
        {!isAuthenticated && (
          <AppBar
            position="static"
            sx={{
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(10px)',
              boxShadow: 'none',
              borderBottom: '1px solid rgba(255,255,255,0.2)',
            }}
          >
            <Toolbar sx={{ px: 2, py: 1 }}>
              {/* Company Logo */}
              <Box sx={{ display: 'flex', alignItems: 'center', mr: 2, width: '100%' }}>
                <a href="https://athrvcloud.com" style={{ textDecoration: 'none', color: 'inherit' }}>
                  <img
                    src={CompanyLogo}
                    alt="Company Logo"
                    style={{ height: 40 }}
                  />
                </a>
              </Box>
              {isMobile ? (
                <>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <IconButton color="inherit">
                      <NotificationsIcon />
                    </IconButton>
                    <IconButton color="inherit">
                      <MailIcon />
                    </IconButton>
                  </Box>
                  {/* Mobile: Hamburger Menu */}
                  <IconButton color="inherit" sx={{ ml: 1 }} onClick={handleMenuOpen}>
                    <MenuIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleMenuClose}
                  >
                    <MenuItem onClick={handleMenuClose} component="a" href="https://athrvcloud.com">
                      Home
                    </MenuItem>
                    <MenuItem onClick={handleMenuClose} component="a" href="https://athrvcloud.com/about-us">
                      About
                    </MenuItem>
                    <MenuItem onClick={handleMenuClose} component="a" href="https://athrvcloud.com/contact">
                      Contact
                    </MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Box sx={{ flexGrow: 1, display: 'flex', gap: 2 }}>
                    <Button
                      href="https://athrvcloud.com"
                      sx={{
                        color: appliedTheme.palette.text.primary,
                        backgroundColor: appliedTheme.palette.primary.main,
                        '&:hover': { backgroundColor: '#002244' },
                      }}
                    >
                      Home
                    </Button>
                    <Button
                      href="https://athrvcloud.com/about-us"
                      sx={{
                        color: appliedTheme.palette.text.primary,
                        backgroundColor: appliedTheme.palette.primary.main,
                        '&:hover': { backgroundColor: '#002244' },
                      }}
                    >
                      About
                    </Button>
                    <Button
                      href="https://athrvcloud.com/contact"
                      sx={{
                        color: appliedTheme.palette.text.primary,
                        backgroundColor: appliedTheme.palette.primary.main,
                        '&:hover': { backgroundColor: '#002244' },
                      }}
                    >
                      Contact
                    </Button>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pl: 2 }}>
                    <IconButton color="inherit">
                      <NotificationsIcon />
                    </IconButton>
                    <IconButton color="inherit">
                      <MailIcon />
                    </IconButton>
                  </Box>
                </>
              )}
              {/* Extra icons for notifications */}

            </Toolbar>
          </AppBar>
        )}

        {/* Main 404 Content */}
        <Container
          sx={{
            flexGrow: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 4,
          }}
        >
          <Paper
            elevation={6}
            sx={{
              p: 4,
              background: 'rgba(255,255,255,0.1)',
              backdropFilter: 'blur(8px)',
              borderRadius: 2,
              textAlign: 'center',
              maxWidth: 600,
            }}
          >
            <Typography variant="h2" sx={{ mb: 2 }}>
              404 - Page Not Found
            </Typography>
            <Typography variant="h5" sx={{ mb: 3 }}>
              Oops! The page you're looking for doesn't exist.
            </Typography>
            <Typography variant="body1" sx={{ mb: 3 }}>
              It might have been moved or deleted. Please check the URL or use the navigation above.
            </Typography>
            {isAuthenticated ? (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: appliedTheme.palette.primary.main,
                  '&:hover': { backgroundColor: '#002244' },
                }}
                onClick={() => navigate('/dashboard')}
              >
                Go to Dashboard
              </Button>
            ) : (
              <Button
                variant="contained"
                sx={{
                  backgroundColor: appliedTheme.palette.primary.main,
                  '&:hover': { backgroundColor: '#002244' },
                }}
                onClick={() => navigate('/login')}
              >
                Please Login to Continue!
              </Button>
            )}
          </Paper>
        </Container>

        {/* Footer: Show only when NOT logged in */}
        {!isAuthenticated && (
          <Box
            component="footer"
            sx={{
              py: 2,
              background: 'rgba(0, 0, 0, 0.6)',
              backdropFilter: 'blur(10px)',
              textAlign: 'center',
            }}
          >
            <Typography variant="body2">
              &copy; {new Date().getFullYear()} Athrv Cloud. All rights reserved.
            </Typography>
          </Box>
        )}
      </Box>
    </ThemeProvider>
  );
};

export default NotFound;
